import type { SequenceToken } from "../types/action";
import type { DebugSaveState, DebugSnapshot } from "../types/debug";
import type { GridPoint, PlayerGridPosition } from "../types/grid";

export type { DebugSaveState, DebugSnapshot } from "../types/debug";

export interface StateLogger {
  log(snapshot: DebugSnapshot): string;
}

function serializeGridPoint(point: GridPoint): GridPoint {
  return {
    x: point.x,
    y: point.y,
  };
}

function serializePlayer(player: PlayerGridPosition): PlayerGridPosition {
  return {
    x: player.x,
    y: player.y,
    facing: player.facing,
  };
}

function serializePlayerNotes(
  notes: Readonly<Record<string, string | null>>,
): Readonly<Record<string, string | null>> {
  const keys = Object.keys(notes).sort((left, right) => left.localeCompare(right));
  const serialized: Record<string, string | null> = {};

  for (const key of keys) {
    serialized[key] = notes[key] ?? null;
  }

  return serialized;
}

function serializeCollectiblesByLevel(
  byLevel: Readonly<Record<string, readonly string[]>>,
): Readonly<Record<string, readonly string[]>> {
  const keys = Object.keys(byLevel).sort((left, right) => left.localeCompare(right));
  const serialized: Record<string, readonly string[]> = {};

  for (const key of keys) {
    serialized[key] = [...(byLevel[key] ?? [])];
  }

  return serialized;
}

function serializeSaveState(saveState: DebugSaveState): DebugSaveState {
  return {
    clearedLevels: [...saveState.clearedLevels],
    perfectedLevels: [...saveState.perfectedLevels],
    unlockedLevels: [...saveState.unlockedLevels],
    unlockedChapters: [...saveState.unlockedChapters],
    passedLocks: [...saveState.passedLocks],
    collectiblesByLevel: serializeCollectiblesByLevel(saveState.collectiblesByLevel),
    permanentRevealLevels: [...saveState.permanentRevealLevels],
  };
}

function createSerializableSnapshot(snapshot: DebugSnapshot): DebugSnapshot {
  return {
    tick: snapshot.tick,
    input: {
      raw: snapshot.input.raw,
      mapped: snapshot.input.mapped,
    },
    result: snapshot.result,
    reason: snapshot.reason,
    ...(snapshot.resultSubtype !== undefined ? { resultSubtype: snapshot.resultSubtype } : {}),
    ...(snapshot.resolutionKind !== undefined ? { resolutionKind: snapshot.resolutionKind } : {}),
    ...(snapshot.sceneName !== undefined ? { sceneName: snapshot.sceneName } : {}),
    ...(snapshot.levelId !== undefined ? { levelId: snapshot.levelId } : {}),
    ...(snapshot.playerPosition !== undefined
      ? { playerPosition: serializePlayer(snapshot.playerPosition) }
      : {}),
    ...(snapshot.truthLine !== undefined ? { truthLine: snapshot.truthLine } : {}),
    ...(snapshot.guessLine !== undefined ? { guessLine: snapshot.guessLine } : {}),
    ...(snapshot.completionState !== undefined
      ? {
          completionState: {
            cleared: snapshot.completionState.cleared,
            perfected: snapshot.completionState.perfected,
          },
        }
      : {}),
    ...(snapshot.fogPermanentlyRevealed !== undefined
      ? { fogPermanentlyRevealed: snapshot.fogPermanentlyRevealed }
      : {}),
    ...(snapshot.debugFogReveal !== undefined
      ? { debugFogReveal: snapshot.debugFogReveal }
      : {}),
    ...(snapshot.debugEnabled !== undefined
      ? { debugEnabled: snapshot.debugEnabled }
      : {}),
    ...(snapshot.saveState !== undefined
      ? { saveState: serializeSaveState(snapshot.saveState) }
      : {}),
    state: {
      player: serializePlayer(snapshot.state.player),
      history: {
        length: snapshot.state.history.length,
        last5: [...snapshot.state.history.last5],
      },
      fog: {
        explored: snapshot.state.fog.explored.map(serializeGridPoint),
        permanentlyRevealed: snapshot.state.fog.permanentlyRevealed,
        debugReveal: snapshot.state.fog.debugReveal,
      },
      collectibles: {
        local: [...snapshot.state.collectibles.local],
        permanent: [...snapshot.state.collectibles.permanent],
      },
      breadcrumbs: {
        active: snapshot.state.breadcrumbs.active.map((b) => ({ ...b })),
        local: [...snapshot.state.breadcrumbs.local],
      },
      symbolSequence: {
        raw: [...snapshot.state.symbolSequence.raw] as SequenceToken[],
        expanded: [...snapshot.state.symbolSequence.expanded],
        playerNotes: serializePlayerNotes(snapshot.state.symbolSequence.playerNotes),
      },
    },
  };
}

export function serializeDebugSnapshot(snapshot: DebugSnapshot): string {
  return JSON.stringify(createSerializableSnapshot(snapshot));
}

export class JsonStateLogger implements StateLogger {
  public log(snapshot: DebugSnapshot): string {
    return serializeDebugSnapshot(snapshot);
  }
}
